const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

/** Extract accountId from the stored JWT without signature verification (read-only claim). */
function getAccountIdFromToken(): string | null {
  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.accountId ? String(payload.accountId) : null;
  } catch {
    return null;
  }
}

async function req(method: string, path: string, body?: Record<string, unknown>) {
  const headers: Record<string, string> = {};
  if (body) headers["Content-Type"] = "application/json";

  // Attach auth token if present (for local dev when Secure cookies don't work)
  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Ignore token retrieval errors
  }

  const resp = await fetch(withBase(path), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await resp.text();
  let data: { error?: string; message?: string; raw?: string; [key: string]: unknown };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = data?.error || data?.message || text || `Request failed (${resp.status})`;
    throw new Error(msg);
  }
  return data;
}

export async function fetchCustomers(includeDeleted = false) {
  const url = `/admin/customers${includeDeleted ? "?includeDeleted=true" : ""}`;
  return req("GET", url);
}

export async function createCustomer(name: string) {
  return req("POST", "/admin/customers/register", { name });
}

export async function updateCustomer(customerId: string, updates: Record<string, unknown>) {
  return req("PUT", `/admin/customers/${encodeURIComponent(customerId)}`, updates);
}

export async function softDeleteCustomer(customerId: string) {
  return req("DELETE", `/admin/customers/${encodeURIComponent(customerId)}`);
}

export async function reactivateCustomer(customerId: string) {
  return req("PUT", `/admin/customers/${encodeURIComponent(customerId)}/reactivate`);
}

export async function impersonateCustomer(customerId: string) {
  return req("POST", `/admin/impersonate/${encodeURIComponent(customerId)}`);
}

export async function createEndUserForCustomer(
  customerId: string,
  email: string,
  firstName?: string,
  lastName?: string
) {
  const body: Record<string, unknown> = { email };
  if (firstName) body.firstName = firstName;
  if (lastName) body.lastName = lastName;
  return req("POST", `/admin/customers/${encodeURIComponent(customerId)}/end-users`, body);
}

export async function fetchEndUsersForCustomer(customerId: string) {
  return req("GET", `/admin/customers/${encodeURIComponent(customerId)}/end-users`);
}

export async function listCustomerUsers() {
  return req("GET", "/customer/customer-users");
}

export async function createCustomerUser(email: string) {
  return req("POST", "/customer/customer-users", { email });
}

export async function listEndUsers() {
  // Returns users with embedded jgsData when available
  return req("GET", "/customer/my-users");
}

export async function customerInfo() {
  return req("GET", "/customer/info");
}

export async function unlinkEndUser(userEmail: string) {
  return req("DELETE", "/customer/users/unlink-jgs-profile", { userEmail });
}

export async function searchJgsStudents(name: string, state?: string, limit: number = 10) {
  // Customer-scoped search endpoint expects "name" and returns an array
  const qs = new URLSearchParams();
  if (name) qs.set("name", name);
  if (state) qs.set("state", state);
  if (limit) qs.set("limit", String(limit));
  const data = await req("GET", `/customer/jgs-students/search?${qs.toString()}`);
  // Support both array and { students: [] } shapes
  if (Array.isArray(data)) return data;
  const dataWithStudents = data as { students?: unknown[] };
  return Array.isArray(dataWithStudents.students) ? dataWithStudents.students : [];
}

export async function linkEndUser(userEmail: string, jgsProfile: string, jgsStudentId?: string) {
  return req("POST", "/customer/users/link-jgs-profile", { userEmail, jgsProfile, jgsStudentId });
}

export async function createLaunchUrl(userEmail: string, audience: string) {
  const API_BASE_URL: string = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
  const resp = await fetch(`${API_BASE_URL}/v1/launch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userEmail, audience }),
  });
  const text = await resp.text();
  let data: { error?: string; message?: string; raw?: string; [key: string]: unknown };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = data?.error || data?.message || text || `Request failed (${resp.status})`;
    throw new Error(msg);
  }
  // Do NOT set recruit_ai_user_role here — launch is acting-as, not a new login.
  // The analytics userType should reflect who the person IS, not who they're viewing as.

  // Tag this session as impersonated for analytics event properties
  try {
    // Save the admin's userId so analytics keeps the admin as the user.
    // Admin sessions don't set recruit_ai_user_id, so fall back to accountId from the JWT.
    const adminUserId = sessionStorage.getItem("recruit_ai_user_id") || getAccountIdFromToken();
    if (adminUserId) sessionStorage.setItem("recruit_ai_admin_user_id", adminUserId);
    sessionStorage.setItem("recruit_ai_is_impersonated", "1");
    const role = sessionStorage.getItem("recruit_ai_user_role") || "unknown";
    sessionStorage.setItem("recruit_ai_impersonated_by", role);
  } catch {}

  return data as { launchUrl: string; userId: string; hasJgsMapping: boolean; isEval?: boolean };
}

export async function stopLaunch() {
  const data = await req("POST", "/v1/launch/stop");
  // Clear impersonation flags so subsequent analytics events are clean
  try {
    sessionStorage.removeItem("recruit_ai_is_impersonated");
    sessionStorage.removeItem("recruit_ai_impersonated_by");
    sessionStorage.removeItem("recruit_ai_admin_user_id");
  } catch {}
  return data;
}

export async function inviteEndUser(email: string) {
  return req("POST", "/customer/invite-end-user", { email });
}

export async function resendInvite(email: string) {
  return req("POST", "/customer/resend-invite", { email });
}

export interface BulkInviteResult {
  email: string;
  status: "success" | "failed" | "email_failed";
  error?: string;
}

export interface BulkInviteResponse {
  totalSubmitted: number;
  successCount: number;
  failedCount: number;
  emailFailedCount: number;
  results: BulkInviteResult[];
}

export async function bulkInviteEndUsers(emails: string[]): Promise<BulkInviteResponse> {
  return req("POST", "/customer/bulk-invite-end-users", { emails }) as Promise<BulkInviteResponse>;
}

export async function getInvitationStats() {
  return req("GET", "/customer/invitation-stats");
}

export async function deleteEndUser(userEmail: string) {
  return req("DELETE", `/customer/end-users/${encodeURIComponent(userEmail)}`);
}

export async function setEndUserType(userEmail: string, userType: "player" | "coach") {
  return req("PUT", "/customer/users/set-type", { userEmail, userType });
}

export async function resetUserOnboarding(userEmail: string) {
  return req("POST", "/customer/users/reset-onboarding", { userEmail });
}

export async function regenerateApiKey(): Promise<{ apiKey: string; apiKeyPrefix: string }> {
  return req("POST", "/customer/regenerate-key") as Promise<{
    apiKey: string;
    apiKeyPrefix: string;
  }>;
}

// Admin user management functions
export async function fetchAdminUsers() {
  return req("GET", "/admin/admin-users");
}

export async function createAdminUser(email: string, password: string) {
  return req("POST", "/admin/admin-users", { email, password });
}

export async function deactivateAdminUser(id: string) {
  return req("DELETE", `/admin/admin-users/${encodeURIComponent(id)}`);
}

export async function updateAdminPassword(id: string, newPassword: string) {
  return req("PUT", `/admin/admin-users/${encodeURIComponent(id)}/password`, { newPassword });
}

// JGS activity sweep — API returns the counters object directly (no wrapper)
export interface JgsSweepCounters {
  profilesScanned: number;
  baselined: number;
  unchanged: number;
  immaterial: number;
  changed: number;
  rolledOver: number;
  skipped: number;
  errors: number;
}

export async function runJgsActivitySweep(): Promise<JgsSweepCounters> {
  return req("POST", "/admin/jgs-change/sweep") as Promise<JgsSweepCounters>;
}

// Search settings
export async function fetchSearchSettings() {
  return req("GET", "/admin/search-settings") as Promise<{
    scoringDifferentialTolerance: number;
    classRankTolerance: number;
  }>;
}

export async function updateSearchSettings(settings: {
  scoringDifferentialTolerance?: number;
  classRankTolerance?: number;
}) {
  return req("PUT", "/admin/search-settings", settings as Record<string, unknown>);
}
